import { createWithEqualityFn as create } from 'zustand/traditional';
import { shallow } from 'zustand/vanilla/shallow';

import SessionService from 'src/services/SessionService';
import UserSevice from 'src/services/UserSevice';
import { getBeginWorkStepOrder } from 'src/utils/beginWork';
import { LEADSMANIA_PRODUCT_CODE, hasLeadsmaniaAccess } from 'src/constants/product';

import type { IUser, IUserDescription, IUserResponse, StepsType } from 'src/types/User';
import type { CurrentSubscription } from 'src/types/Subscription';

type IState = IUser;

interface IAction {
    setOnboardingInfo: (onboardingStep: StepsType, isOnboardingCompleted: boolean) => void;
    updateStep: (data: StepsType) => Promise<unknown>;
    completeOnboarding: () => Promise<unknown>;

    confirmEmail: (code: string) => Promise<unknown>;
    setEmail: (email: string) => void;
    refetchEmailCode: () => Promise<void>;

    clearUser: () => void;

    setUserInfo: (values: IUserResponse) => void;
    getUserInfo: () => IState;
    updateSubscription: (value: CurrentSubscription | null) => void;
    updateDescription: (data: IUserDescription) => Promise<unknown>;
    updateNotificationSettings: (settings: {
        tg_notifications_enabled?: boolean;
        tg_notification_mode?: 'all' | 'relevant';
    }) => void;
}

const useUserStore = create<IState & IAction>(
    (set, get) => ({
        id: 0,

        role: '',

        username: '',

        email: '',

        bonusPoints: 0,

        isEmailConfirmed: false,

        confirmEmailLoading: false,

        isOnboardingCompleted: false,

        onboardingStep: 1,
        onboardingStepName: 'email_confirm' as StepsType,

        isAdminOverride: false,

        current_subscription: null,
        products: [LEADSMANIA_PRODUCT_CODE],
        current_product: LEADSMANIA_PRODUCT_CODE,
        currentProduct: LEADSMANIA_PRODUCT_CODE,
        isLeadsmaniaIntroCompleted: false,
        isLeadsmaniaQuestionnaireBonusAwarded: false,
        walletBalanceRub: 0,
        monthlySpendRub: 0,
        balanceRunoutDate: null,
        activePublicBundlesCount: 0,
        activeChatsCount: 0,
        activeKeywordsCount: 0,

        first_name: '',
        tg_account: '',
        business_description: '',
        website_url: '',
        crm_system: '',
        lead_delivery_channels: [],
        lead_delivery_other: '',
        phone: '',
        user_role: '',
        tg_notification_mode: 'all',
        niche_analysis: null,

        setEmail: (email: string) => set({ email }),

        clearUser: () =>
            set({
                id: 0,
                email: '',
                isOnboardingCompleted: false,
                isEmailConfirmed: false,
                onboardingStep: 1,
                onboardingStepName: 'email_confirm',
                current_subscription: null,
                products: [LEADSMANIA_PRODUCT_CODE],
                current_product: LEADSMANIA_PRODUCT_CODE,
                currentProduct: LEADSMANIA_PRODUCT_CODE,
                isLeadsmaniaIntroCompleted: false,
                isLeadsmaniaQuestionnaireBonusAwarded: false,
                walletBalanceRub: 0,
                monthlySpendRub: 0,
                balanceRunoutDate: null,
                activePublicBundlesCount: 0,
                activeChatsCount: 0,
                activeKeywordsCount: 0,
                first_name: '',
                tg_account: '',
                business_description: '',
                website_url: '',
                crm_system: '',
                lead_delivery_channels: [],
                lead_delivery_other: '',
                phone: '',
                user_role: '',
                tg_notification_mode: 'all',
            }),

        confirmEmail: async (code) => {
            try {
                await SessionService.confirmEmail({
                    code,
                    email: get().email,
                });

                set({ isEmailConfirmed: true });

                return Promise.resolve();
            } catch (error) {
                console.log(error);

                return Promise.reject(error);
            }
        },

        refetchEmailCode: async () => {
            try {
                await SessionService.refetchEmailCode();
                return Promise.resolve();
            } catch (error) {
                console.log(error);
                return Promise.reject(error);
            }
        },

        updateStep: async (data) => {
            const { onboardingStep, onboardingStepName, isOnboardingCompleted } = get();
            try {
                if (isOnboardingCompleted) return;

                // НЕ меняем шаг в store до успешного ответа от сервера
                // Это предотвращает проблемы с синхронизацией
                console.log(
                    `📤 [user.ts] Отправляем запрос на обновление шага: ${data} (текущий шаг: ${onboardingStepName})`
                );

                const { onboarding_step, is_onboarding_completed } = await UserSevice.updateOnboardingStep(data);

                console.log(`✅ [user.ts] Шаг успешно обновлен: ${onboarding_step}`);

                if (!is_onboarding_completed) {
                    set({
                        onboardingStep: getBeginWorkStepOrder(onboarding_step, onboardingStep),
                        onboardingStepName: onboarding_step || 'email_confirm',
                        isOnboardingCompleted: is_onboarding_completed,
                    });
                } else {
                    set({
                        onboardingStep: getBeginWorkStepOrder('completed', onboardingStep),
                        onboardingStepName: 'completed',
                        isOnboardingCompleted: true,
                    });
                }

                return Promise.resolve();
            } catch (error) {
                console.error(`❌ [user.ts] Ошибка обновления шага:`, error);
                // Не меняем шаг в store при ошибке
                return Promise.reject(error);
            }
        },

        completeOnboarding: async () => {
            try {
                await UserSevice.updateOnboardingStep('completed');
                set({
                    isOnboardingCompleted: true,
                    onboardingStep: getBeginWorkStepOrder('completed', get().onboardingStep),
                    onboardingStepName: 'completed',
                });
                return Promise.resolve();
            } catch (error) {
                return Promise.reject(error);
            }
        },

        setOnboardingInfo: (onboardingStep, isOnboardingCompleted) => {
            set({
                onboardingStep: getBeginWorkStepOrder(onboardingStep, get().onboardingStep),
                onboardingStepName: onboardingStep,
                isOnboardingCompleted,
            });
        },

        setUserInfo: ({
            bonus_points: bonusPoints,
            is_email_confirmed: isEmailConfirmed,
            is_onboarding_completed: isOnboardingCompleted,
            onboarding_step,
            is_admin_override: isAdminOverride,
            current_subscription,
            products,
            current_product,
            leadsmania_intro_completed,
            leadsmania_questionnaire_bonus_awarded,
            wallet_balance_rub,
            monthly_spend_rub,
            balance_runout_date,
            active_public_bundles_count,
            active_chats_count,
            active_keywords_count,
            first_name,
            tg_account,
            business_description,
            website_url,
            crm_system,
            lead_delivery_channels,
            lead_delivery_other,
            phone,
            user_role,
            tg_notification_mode = 'all',
            niche_analysis,
            ...values
        }: IUserResponse) => {
            const normalizedProducts =
                Array.isArray(products) && products.length ? products : [LEADSMANIA_PRODUCT_CODE];
            const normalizedCurrentProduct = current_product || LEADSMANIA_PRODUCT_CODE;
            const isLeadsmaniaSession = hasLeadsmaniaAccess(normalizedProducts, normalizedCurrentProduct);
            // Нормализация текущей подписки: если есть fixed_price > 0 — подставляем его как цену плана
            let normalizedCurrent = isLeadsmaniaSession ? null : (current_subscription as unknown as any);
            if (normalizedCurrent && normalizedCurrent.plan) {
                const fixed = Number(normalizedCurrent.fixed_price ?? NaN);
                // Используем plan.price как строку или число, конвертируем в число
                const planPriceStr = normalizedCurrent.plan?.price ?? '0';
                const planPriceNum = Number(planPriceStr) || 0;
                // Если fixed_price > 0, используем его, иначе используем plan.price
                const effective = Number.isFinite(fixed) && fixed > 0 ? fixed : planPriceNum;
                normalizedCurrent = {
                    ...normalizedCurrent,
                    plan: {
                        ...normalizedCurrent.plan,
                        price: effective,
                        // протащим фикс в план, если бэк его не шлёт
                        fixed_price: normalizedCurrent.plan?.fixed_price ?? normalizedCurrent.fixed_price,
                    },
                };
            }
            set({
                ...values,
                isEmailConfirmed,
                bonusPoints,
                isOnboardingCompleted,
                onboardingStep: getBeginWorkStepOrder(onboarding_step, 1),
                onboardingStepName: onboarding_step || 'email_confirm',
                isAdminOverride,
                current_subscription: normalizedCurrent as unknown as CurrentSubscription | null,
                products: normalizedProducts,
                current_product: normalizedCurrentProduct,
                currentProduct: normalizedCurrentProduct,
                leadsmania_intro_completed,
                isLeadsmaniaIntroCompleted: Boolean(leadsmania_intro_completed),
                leadsmania_questionnaire_bonus_awarded,
                isLeadsmaniaQuestionnaireBonusAwarded: Boolean(leadsmania_questionnaire_bonus_awarded),
                wallet_balance_rub,
                walletBalanceRub: Number(wallet_balance_rub ?? 0) || 0,
                monthly_spend_rub,
                monthlySpendRub: Number(monthly_spend_rub ?? 0) || 0,
                balance_runout_date,
                balanceRunoutDate: balance_runout_date || null,
                active_public_bundles_count,
                activePublicBundlesCount: Number(active_public_bundles_count ?? 0) || 0,
                active_chats_count,
                activeChatsCount: Number(active_chats_count ?? 0) || 0,
                active_keywords_count,
                activeKeywordsCount: Number(active_keywords_count ?? 0) || 0,
                first_name: first_name || '',
                tg_account: tg_account || '',
                business_description: business_description || '',
                website_url: website_url || '',
                crm_system: crm_system || '',
                lead_delivery_channels: Array.isArray(lead_delivery_channels) ? lead_delivery_channels : [],
                lead_delivery_other: lead_delivery_other || '',
                phone: phone || '',
                user_role: user_role || '',
                tg_notification_mode,
                niche_analysis: niche_analysis || null,
            });
        },

        getUserInfo: () => {
            const {
                id,
                email,
                username,
                role,

                bonusPoints,
                isEmailConfirmed,
                isOnboardingCompleted,
                onboardingStep,
                isAdminOverride,
                current_subscription,
                products,
                currentProduct,
                isLeadsmaniaIntroCompleted,
                isLeadsmaniaQuestionnaireBonusAwarded,
                walletBalanceRub,
                monthlySpendRub,
                balanceRunoutDate,
                activePublicBundlesCount,
                activeChatsCount,
                activeKeywordsCount,
                first_name,
                tg_account,
                business_description,
                phone,
                lead_delivery_channels,
                lead_delivery_other,
                user_role,
                tg_notification_mode,
                niche_analysis,
            } = get();
            return {
                id,
                email,
                username,
                role,

                bonusPoints,
                isOnboardingCompleted,
                isEmailConfirmed,
                onboardingStep,
                isAdminOverride,
                current_subscription,
                products,
                currentProduct,
                isLeadsmaniaIntroCompleted,
                isLeadsmaniaQuestionnaireBonusAwarded,
                walletBalanceRub,
                monthlySpendRub,
                balanceRunoutDate,
                activePublicBundlesCount,
                activeChatsCount,
                activeKeywordsCount,
                first_name,
                tg_account,
                business_description,
                phone,
                lead_delivery_channels,
                lead_delivery_other,
                user_role,
                tg_notification_mode,
                niche_analysis,
            };
        },

        updateSubscription: (value: CurrentSubscription | null) => {
            set({
                current_subscription: value,
            });
        },

        updateDescription: (data: IUserDescription) => {
            try {
                UserSevice.updateDescription({
                    tg_account: data.tg_account,
                    business_description: data.business_description,
                    first_name: data.first_name,
                    phone: data.phone,
                    lead_delivery_channels: data.lead_delivery_channels,
                    lead_delivery_other: data.lead_delivery_other,
                    user_role: data.user_role,
                    website_url: data.website_url,
                    crm_system: data.crm_system,
                });

                set({
                    tg_account: data.tg_account,
                    business_description: data.business_description,
                    first_name: data.first_name,
                    phone: data.phone || '',
                    lead_delivery_channels: data.lead_delivery_channels || [],
                    lead_delivery_other: data.lead_delivery_other || '',
                    user_role: data.user_role || '',
                    website_url: data.website_url || '',
                    crm_system: data.crm_system || '',
                });

                return Promise.resolve();
            } catch (error) {
                console.log('updateDescription:', error);
                return Promise.reject(error);
            }
        },

        updateNotificationSettings: (settings: {
            tg_notifications_enabled?: boolean;
            tg_notification_mode?: 'all' | 'relevant';
        }) => {
            set((state) => ({
                ...state,
                ...settings,
            }));
        },
    }),
    shallow
);

export default useUserStore;
